/* ============================================================
   Dathent — socialAddon.js (TASK-329, EPIC-029 #409/#521)
   Pure helpers behind the Scheduler add-on for social accounts beyond a
   plan's included count (owner decision 2026-09-07): "N connected · <Plan>
   includes K -> +(N-K) x €3.99 = €X/mo". No React, no fetch — testable
   under plain vitest, mirroring lib/postingPlan.js's own pure/tested shape.

   Reuses formatPostingMoney (the ONE money formatter already used for the
   Scheduler/posting price lines) rather than forking a second currency
   formatter — see postingPlan.js's own header for its conventions.

   ROUND 2 (adversarial-review fix, slice B r2 CONTRACT UPDATE): the
   `socialAccounts` field group on GET /api/billing/entitlements now comes
   from the stripe quote and carries a `reason` (flag_off | not_billable |
   no_active_subscription | schedule_managed | price_unavailable |
   posting_plan); `unitAmount`/`currency` may be NULL (no price configured
   yet). `resolveSocialAddonState` is the ONE gate every screen/component
   reads through — it decides between three mutually-exclusive render modes
   so nothing downstream can independently reinvent (and drift from) the
   billable/reason/null-price logic:
     - 'hidden'   the field group itself is absent, or reason === 'flag_off'
                  -> render NOTHING new (byte-identical to pre-TASK-329).
     - 'includes' billable !== true (any other reason), OR unitAmount/
                  currency is missing/malformed -> the plain "<Plan>
                  includes K" line only. No chip, no sheet, no price
                  anywhere, no confirm-flow relaxation.
     - 'priced'   billable === true AND unitAmount is a positive integer
                  AND currency is a non-empty string -> the full
                  provenance/chip/sheet/confirm-relaxation experience.
   `resolveSocialAddonPricing` takes that state's shape (mode +
   connected/included/unitAmount/currency) and must never fabricate a
   price — outside 'priced' mode it always renders the plain includes line,
   regardless of what a caller passes for unitAmount/currency.
   ============================================================ */
import { formatPostingMoney } from './postingPlan.js';

// TASK-329: fallback-never-blank mirror of entitlement-plans.ts's
// social_accounts_total per NEW_PLAN_DEFS id (lib/plans.js) — the SAME
// number already baked into each card's "1 brand, N channels" feature
// bullet, used here only when GET /api/billing/plans hasn't loaded yet or a
// plan's admin-managed limits_json is missing the field (same
// fallback-never-blank convention as usePlanCatalog.js's own price/promo
// fields). Used for a CANDIDATE plan the org has not switched to yet — the
// org's CURRENT plan's included count always comes from entitlements'
// socialAccounts field group instead (slice B resolves it live, admin-
// override- and trial-aware).
const NEW_PLAN_SOCIAL_ACCOUNTS_INCLUDED_DEFAULT = {
  starter: 3,
  growth: 6,
  pro: 12,
  agency: 30,
};

/**
 * Resolves a CANDIDATE plan's included social-account count for the
 * provenance line under its AI card (PlanSelectScreen/Plans.jsx) —
 * `catalogLimits` is `usePlanCatalog().getNewPlanDisplay(planId).limits`
 * (the admin-managed `billing_plans.limits_json` row, when the catalog has
 * loaded and that plan's row sets the field). `null` is a real "unlimited"
 * answer (e.g. a future enterprise-tier card); only an outright MISSING
 * field falls back to the static default above.
 */
export function resolveIncludedForPlanCard(planId, catalogLimits) {
  if (catalogLimits && Object.prototype.hasOwnProperty.call(catalogLimits, 'social_accounts_total')) {
    const v = catalogLimits.social_accounts_total;
    if (v === null || Number.isInteger(v)) return v;
  }
  const fallback = NEW_PLAN_SOCIAL_ACCOUNTS_INCLUDED_DEFAULT[planId];
  return Number.isInteger(fallback) ? fallback : null;
}

// The one reason value that means "this org/product shouldn't see anything
// TASK-329 added at all" — everything else (including an unrecognised
// future reason) degrades to 'includes', never to 'hidden', so a backend
// addition can't accidentally go silent instead of fail-soft-visible.
const HIDDEN_REASON = 'flag_off';

/**
 * THE gate (round 2 fix, blockers 1/2/3/5): resolves the entitlements
 * `socialAccounts` field group (slice B) into exactly one of three
 * mutually-exclusive render modes — see this file's header. Every screen
 * wires through this ONE function instead of re-deriving `billable`/
 * `reason`/null-price checks locally, so Settings' Billing tab, its
 * Connected-accounts tab, Plans.jsx, PlanSelectScreen and the onboarding/
 * activation connect flows can never disagree about which mode an org is
 * in.
 *
 * `social` is the raw field group (or null/undefined — no org context, or
 * the best-effort backend lookup failed, same fail-soft-to-hidden contract
 * as every other best-effort field on GET /api/billing/entitlements).
 */
export function resolveSocialAddonState(social) {
  if (!social || typeof social !== 'object' || social.reason === HIDDEN_REASON) {
    return {
      mode: 'hidden', connected: 0, included: null, extra: 0,
      unitAmount: null, currency: null, reason: social?.reason ?? null,
    };
  }
  const connected = Number.isFinite(social.connected) && social.connected > 0
    ? Math.floor(social.connected) : 0;
  const included = (social.included === null || social.included === undefined)
    ? null
    : Math.max(0, Math.floor(Number(social.included)) || 0);
  // A positive integer only — zero/negative/non-integer is treated the same
  // as "no real price yet" (price_unavailable in spirit, whatever the
  // backend labelled it), never coerced into a bogus free/negative charge.
  const unitAmount = Number.isInteger(social.unitAmount) && social.unitAmount > 0
    ? social.unitAmount : null;
  const currency = typeof social.currency === 'string' && social.currency ? social.currency : null;
  const billable = social.billable === true;
  const mode = (billable && unitAmount !== null && currency !== null) ? 'priced' : 'includes';
  const extra = (mode === 'priced' && included !== null) ? Math.max(0, connected - included) : 0;
  return {
    mode, connected, included, extra,
    unitAmount: mode === 'priced' ? unitAmount : null,
    currency: mode === 'priced' ? currency : null,
    reason: social.reason ?? null,
  };
}

/**
 * Resolves the display numbers + i18n copy for the price-provenance line
 * shown under an AI plan card / the current-plan card (PlanSelectScreen,
 * Plans.jsx, Settings.jsx BillingTab): "N connected · <Plan> includes K ->
 * +(N-K) x unit = total/mo", or the plain "<Plan> includes K" when nothing
 * is billed beyond the plan (extra === 0) OR `mode` isn't 'priced'.
 *
 * `mode` should come from `resolveSocialAddonState` (or be recomputed the
 * same way per-card, e.g. Plans.jsx/PlanSelectScreen.jsx swapping in a
 * candidate plan's own `included`) — round 2 fix: this function no longer
 * defaults a missing `unitAmount`/`currency` to the catalog price point.
 * Outside 'priced' mode (or with a malformed unitAmount/currency even when
 * `mode: 'priced'` was passed in error) it ALWAYS renders the plain
 * includes line — it never fabricates a price.
 *
 * `t` is the i18next translate function. Omit it to get only the raw
 * numbers back (connectedSegment/provenanceLine/includesLine stay null) —
 * e.g. for a unit test that doesn't want to stub i18next.
 */
export function resolveSocialAddonPricing({
  mode = 'includes',
  connected,
  included,
  unitAmount = null,
  currency = null,
  planName = '',
  locale = 'en',
  t,
} = {}) {
  const safeConnected = Number.isFinite(connected) && connected > 0 ? Math.floor(connected) : 0;
  const safeIncluded = (included === null || included === undefined)
    ? null
    : Math.max(0, Math.floor(Number(included)) || 0);
  const isPriced = mode === 'priced'
    && Number.isInteger(unitAmount) && unitAmount > 0
    && typeof currency === 'string' && currency;
  const safeUnitAmount = isPriced ? unitAmount : null;
  const safeCurrency = isPriced ? currency : null;
  const extra = (isPriced && safeIncluded !== null) ? Math.max(0, safeConnected - safeIncluded) : 0;
  const monthlyDelta = isPriced ? safeUnitAmount * extra : null;
  const unitFormatted = isPriced ? formatPostingMoney(safeUnitAmount, safeCurrency, locale) : null;
  const monthlyDeltaFormatted = isPriced ? formatPostingMoney(monthlyDelta, safeCurrency, locale) : null;

  const result = {
    mode: isPriced ? 'priced' : 'includes',
    connected: safeConnected,
    included: safeIncluded,
    extra,
    unitAmount: safeUnitAmount,
    unitFormatted,
    monthlyDelta,
    monthlyDeltaFormatted,
    planName,
    connectedSegment: null,
    provenanceSuffix: null,
    provenanceLine: null,
    includesLine: null,
  };
  if (typeof t !== 'function') return result;

  result.connectedSegment = t('socialAddon.connectedSegment', { count: safeConnected });
  result.includesLine = safeIncluded === null
    ? t('socialAddon.includesUnlimitedLine', { plan: planName })
    : t('socialAddon.includesLine', { plan: planName, included: safeIncluded });
  // Kept as its own field (not just baked into provenanceLine below) so a
  // caller rendering the "N connected" segment as a CLICKABLE button (owner
  // decision: it opens <ConnectedAccountsSheet> only when extra > 0) can
  // compose the two pieces itself with the button in between, instead of
  // parsing them back out of one joined string. Never populated outside
  // 'priced' mode (nothing to price).
  result.provenanceSuffix = isPriced
    ? t('socialAddon.provenanceSuffix', {
        plan: planName,
        included: safeIncluded,
        extra,
        unit: unitFormatted,
        total: monthlyDeltaFormatted,
      })
    : null;
  result.provenanceLine = (isPriced && extra > 0)
    ? `${result.connectedSegment} · ${result.provenanceSuffix}`
    : result.includesLine;
  return result;
}

/**
 * True only when this pricing actually bills a Scheduler add-on — the gate
 * for showing the "N connected" segment as a clickable button that opens
 * <ConnectedAccountsSheet> (owner decision: the sheet appears "ONLY when
 * there is overpricing beyond the chosen plan", never by default). The ONE
 * place that gate is expressed — callers (SocialAddonProvenanceLine,
 * PlanSelectScreen's non-interactive-card fallback link) read it instead of
 * re-checking `pricing.extra > 0` themselves.
 */
export function shouldOfferAccountsSheet(pricing) {
  return Boolean(pricing && pricing.mode === 'priced' && pricing.extra > 0);
}

/**
 * Mirrors auth-worker's assertSocialAccountCapacity / social-account-
 * capacity.ts allow-over-limit predicate on the CLIENT side — "would the
 * NEXT connect push this org past its plan's included count". Used to
 * decide whether the generalised SocialConnectConfirm dialog (not the hard-
 * block cap banner) should gate a connect click. `social` is the
 * `socialAccounts` field group from GET /api/billing/entitlements
 * (slice B) — null/undefined (no org context, or the best-effort lookup
 * failed) safely resolves to false, same fail-soft-to-off philosophy as
 * every other best-effort field on that response.
 *
 * Round 2 fix (blocker 1/5): resolves through `resolveSocialAddonState`
 * itself now, so it is true ONLY in 'priced' mode — an 'includes' org
 * (free/no-card/lapsed/no-price-yet) keeps today's hard-cap banner instead
 * of ever reaching the confirm dialog, and callers no longer need to
 * separately guard on `social?.billable` before calling this (a duplicate
 * gate that could drift from this function's own logic).
 */
export function wouldExceedIncluded(social) {
  const state = resolveSocialAddonState(social);
  if (state.mode !== 'priced' || state.included === null) return false;
  return state.connected >= state.included;
}

/**
 * Generalises buildPostingDisconnectCopy (postingPlan.js) for the add-on
 * case — a disconnect that actually drops the org's overage, so the bill
 * changes (unlike a disconnect while comfortably under the included count,
 * which keeps the existing plain confirm copy at the call site — callers
 * only use this when the account being removed counts toward `extra`,
 * i.e. only ever in 'priced' mode with `extra > 0` already).
 */
export function buildSocialAddonDisconnectCopy({
  label,
  connected,
  included,
  unitAmount,
  currency,
  planName,
  locale = 'en',
  t,
} = {}) {
  const newConnected = Math.max(0, Math.floor(Number(connected) || 0) - 1);
  const pricing = resolveSocialAddonPricing({
    mode: 'priced', connected: newConnected, included, unitAmount, currency, planName, locale, t,
  });
  return {
    newConnected,
    pricing,
    message: t('socialAddon.disconnectConfirm', { label, newTotal: pricing.monthlyDeltaFormatted }),
  };
}

/**
 * Parses/validates the GET /api/billing/social-addon/quote response
 * (stripe-worker handlers/social-addon-quote.ts) into the shape
 * SocialConnectConfirm renders, formatting money via formatPostingMoney
 * (same reuse rule as resolveSocialAddonPricing above) rather than trusting
 * raw cents. Returns null for anything unparseable/malformed, mirroring
 * postingPlan.js's formatPostingQuote "null = show the generic error state"
 * contract.
 *
 * Round 2 fix (blocker 4): also resolves a `mode` — 'priced' only when
 * `billable === true` AND a real (positive-integer) `unitAmount` AND a
 * real (non-fallback) `currency` came back; otherwise 'includes'.
 * SocialConnectConfirm reads this instead of trusting the LIVE quote to
 * always be priced just because the caller's entitlements snapshot looked
 * priced at gate-check time — the fresh quote is the source of truth, and
 * outside 'priced' mode none of the *Formatted fields describe a real
 * price (unitFormatted/monthlyTotalFormatted/monthlyDeltaFormatted are all
 * null), so the confirm dialog can never render a fabricated number.
 */
export function parseSocialAddonQuote(input, locale = 'en') {
  const quote = input?.data && typeof input.data === 'object' ? input.data : input;
  if (!quote || typeof quote !== 'object') return null;
  const intFields = ['connected', 'extraNow', 'extraAfter'];
  if (intFields.some((key) => !Number.isInteger(quote[key]) || quote[key] < 0)) return null;
  if (quote.included !== null && !Number.isInteger(quote.included)) return null;
  const unitAmount = Number.isInteger(quote.unitAmount) && quote.unitAmount > 0 ? quote.unitAmount : null;
  const currencyProvided = typeof quote.currency === 'string' && quote.currency ? quote.currency : null;
  const currency = currencyProvided || 'eur';
  const billable = quote.billable === true;
  const mode = (billable && unitAmount !== null && currencyProvided !== null) ? 'priced' : 'includes';
  const monthlyTotal = mode === 'priced' ? unitAmount * quote.extraAfter : null;
  const monthlyDelta = mode === 'priced' && Number.isInteger(quote.monthlyDelta) ? quote.monthlyDelta : null;
  return {
    ...quote,
    mode,
    // Nothing billed by THIS connect (stays within, or already was within,
    // the plan's included count) — drives the "Included in your plan" copy.
    // True regardless of mode: an unpriced org can still genuinely stay
    // within its included count.
    noChange: quote.extraAfter === 0,
    unitFormatted: mode === 'priced' ? formatPostingMoney(unitAmount, currency, locale) : null,
    monthlyTotalFormatted: mode === 'priced' ? formatPostingMoney(monthlyTotal, currency, locale) : null,
    monthlyDeltaFormatted: mode === 'priced' && monthlyDelta !== null
      ? formatPostingMoney(monthlyDelta, currency, locale)
      : null,
  };
}

// TASK-329 round 4 fix (auth-worker guard contract, 2nd review pass):
// isBilledConnectConfirm — the pure "was this branch actually billed"
// helper this function used to sit next to — is REMOVED. The auth-worker
// guard turned out to require `overage_ack=1` for EVERY connect on the
// Scheduler (posting) plan and every add-on overage check, not just the
// branches that render a price (the floored first Scheduler connect
// (quote.noChange) and a drifted/decreased quantity (quote.noIncrease)
// still went through the guard and, without an ack, 302'd back to Settings
// in a loop). SocialConnectConfirm.jsx's onContinue now sends
// `overageAck: true` unconditionally on every Continue click — the confirm
// sheet itself IS the price screen, so whatever branch it rendered, the
// user saw it before continuing. Nothing else in the codebase consumed
// isBilledConnectConfirm (only SocialConnectConfirm.jsx's own onContinue
// did), so it was deleted outright along with its dedicated test block in
// socialAddon.test.js rather than left as dead exported API.

// TASK-329 round 3 fix (BLOCKER): re-auth of an account the org ALREADY
// has connected must never be treated as a new connect. Settings.jsx's
// "Reconnect" button only ever renders for these three raw backend
// statuses (SocialAccountStatus in services/api/types.ts) — the account
// row still exists and still counts toward the org's connected total, so
// re-authorizing it adds nothing new to bill (Settings.jsx's own
// handleReconnect comment: "Reauthorization does not add a billed
// social, so it needs no add=1 quote"). Only bringing back a genuinely
// 'revoked'/'disconnected' row — which does NOT count toward the total
// any more — is gated like a brand-new connect (CONTRACT UPDATE, slice B
// r2: "Reconnect of a revoked account is now gated like a new connect").
// Misc.jsx's social-page collapses all of expired/revoked/failed into one
// mapped 'reconnect' UI status for its status pill/count — this resolves
// off the RAW backend status instead, so that collapsing can't blur the
// billing-relevant distinction.
const REAUTH_OF_CONNECTED_STATUSES = new Set(['needs_reconnect', 'expired', 'failed']);

export function isReauthOfConnectedAccount(status) {
  return REAUTH_OF_CONNECTED_STATUSES.has(String(status || '').toLowerCase());
}
